import { supabase } from '@/lib/supabase';
import { imageService } from './image';

export interface Banner {
  id: string;
  title?: string;
  description?: string;
  image_filename: string; // 파일명만 저장 (adbanner1.jpg, adbanner2.jpg)
  link_url?: string;
  is_active: boolean;
  display_order: number;
  start_date?: string;
  end_date?: string;
  created_at: string;
}

export interface BannerWithUrl extends Omit<Banner, 'image_filename'> {
  image_url: string; // ImageService에서 생성된 전체 URL
  image_filename: string; // 원본 파일명도 유지
}

class BannerService {
  
  /**
   * 활성화된 배너 목록 가져오기
   * is_active = true이고 날짜 범위 내의 배너들을 display_order 순으로 정렬
   */
  async getActiveBanners(): Promise<{ success: boolean; data?: BannerWithUrl[]; error?: string }> {
    try {
      const now = new Date().toISOString();
      
      let query = supabase
        .from('banners')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      // 날짜 범위 필터링 (start_date와 end_date가 설정된 경우만)
      // start_date가 null이거나 현재보다 이전이고, end_date가 null이거나 현재보다 이후인 경우
      query = query.or(`start_date.is.null,start_date.lte.${now}`);
      query = query.or(`end_date.is.null,end_date.gte.${now}`);

      const { data, error } = await query;

      if (error) {
        console.error('Get active banners error:', error);
        return { success: false, error: error.message };
      }

      // ImageService를 통해 전체 URL 생성
      const bannersWithUrls: BannerWithUrl[] = (data || []).map(banner => {
        // 이미 전체 URL인 경우 그대로 사용, 파일명만 있는 경우 URL 생성
        const isFullUrl = banner.image_url.startsWith('http');
        return {
          ...banner,
          image_url: isFullUrl ? banner.image_url : imageService.getBannerUrl(banner.image_url),
          image_filename: isFullUrl ? banner.image_url.split('/').pop() || banner.image_url : banner.image_url
        };
      });

      return { success: true, data: bannersWithUrls };
    } catch (error) {
      console.error('Get active banners error:', error);
      return { success: false, error: '배너 목록을 불러오는 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 모든 배너 목록 가져오기 (관리자용)
   */
  async getAllBanners(): Promise<{ success: boolean; data?: BannerWithUrl[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('banners')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Get all banners error:', error);
        return { success: false, error: error.message };
      }

      // ImageService를 통해 전체 URL 생성
      const bannersWithUrls: BannerWithUrl[] = (data || []).map(banner => {
        // 이미 전체 URL인 경우 그대로 사용, 파일명만 있는 경우 URL 생성
        const isFullUrl = banner.image_url.startsWith('http');
        return {
          ...banner,
          image_url: isFullUrl ? banner.image_url : imageService.getBannerUrl(banner.image_url),
          image_filename: isFullUrl ? banner.image_url.split('/').pop() || banner.image_url : banner.image_url
        };
      });

      return { success: true, data: bannersWithUrls };
    } catch (error) {
      console.error('Get all banners error:', error);
      return { success: false, error: '배너 목록을 불러오는 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 배너 생성 (관리자용)
   */
  async createBanner(bannerData: {
    title?: string;
    description?: string;
    image_filename: string;
    link_url?: string;
    display_order: number;
    start_date?: string;
    end_date?: string;
  }): Promise<{ success: boolean; data?: Banner; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('banners')
        .insert({
          title: bannerData.title,
          description: bannerData.description,
          image_url: bannerData.image_filename, // 파일명만 저장
          link_url: bannerData.link_url,
          is_active: true,
          display_order: bannerData.display_order,
          start_date: bannerData.start_date,
          end_date: bannerData.end_date
        })
        .select()
        .single();

      if (error) {
        console.error('Create banner error:', error);
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Create banner error:', error);
      return { success: false, error: '배너 생성 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 배너 활성화/비활성화 토글
   */
  async toggleBannerStatus(id: string, isActive: boolean): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('banners')
        .update({ is_active: isActive })
        .eq('id', id);

      if (error) {
        console.error('Toggle banner status error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Toggle banner status error:', error);
      return { success: false, error: '배너 상태 변경 중 오류가 발생했습니다.' };
    }
  }

  /**
   * 배너 삭제
   */
  async deleteBanner(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('banners')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Delete banner error:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Delete banner error:', error);
      return { success: false, error: '배너 삭제 중 오류가 발생했습니다.' };
    }
  }
}

export const bannerService = new BannerService();